<script setup lang="ts">
  import { provide, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useUser } from '@gat-ship/user'
  import { accessCodes } from '@gat-ship/types'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import {
    agencyNodeSelectKey,
    AgencyStructureList,
    AgencyStructureModalProvider,
    AgencyStructureProvider
  } from '@gat-ship/agency'
  import { useAdminTabClass } from '../../composables/useAdminTabClass'

  // The global AdministrationModal opens purely off `?admin=agency-structure` and
  // is mounted for every authenticated user, so this panel must gate itself — the
  // router AgencyStructureView enforces the same check via PageLayout access-props.
  const user = useUser()

  // Local (non-deep-linked) view state, mirroring the routed view's Active /
  // Inactive tabs. Kept off the URL so it doesn't clobber the route view's own
  // `?show` query param.
  const activeTab = ref<'agency-structure' | 'deactivated-agencies'>(
    'agency-structure'
  )

  const route = useRoute()
  const router = useRouter()

  // From the AdministrationModal panel, an agency-row click opens the
  // self-contained agency detail modal (driven by `?agency` via the global
  // AgencyDetailModalProvider), layered over the admin modal. Provided to the
  // structure tree so the recursive list needn't thread an emit.
  function openAgency(agencyId: string) {
    router.push({
      query: {
        ...route.query,
        agency: agencyId,
        agencySection: 'general'
      }
    })
  }

  provide(agencyNodeSelectKey, openAgency)

  const tabClass = useAdminTabClass(activeTab, 'pill')
</script>

<template>
  <div
    v-if="!user.hasPermission(accessCodes.system.agency.viewAgencyStructure)"
    class="text-muted-foreground p-10 text-center text-sm"
  >
    You don't have permission to view the agency structure.
  </div>
  <AgencyStructureProvider v-else fetch-on-mount>
    <AgencyStructureModalProvider>
      <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
           row off the modal edge (the tenant-users panel precedent). -->
      <div class="flex flex-col gap-6 pb-5 pt-3">
        <ModalSection>
          <ModalSectionHeader class="pt-1">
            <ModalSectionHeaderTitle>Agency Structure</ModalSectionHeaderTitle>
            <ModalSectionHeaderDescription>
              Manage agencies and use groups to build hierarchies that model
              your agency business.
            </ModalSectionHeaderDescription>
          </ModalSectionHeader>
          <div class="flex items-center gap-2">
            <div class="bg-muted flex items-center gap-1 rounded-lg p-1">
              <button
                type="button"
                :class="tabClass('agency-structure')"
                @click="activeTab = 'agency-structure'"
              >
                Active
              </button>
              <button
                type="button"
                :class="tabClass('deactivated-agencies')"
                @click="activeTab = 'deactivated-agencies'"
              >
                Inactive
              </button>
            </div>
          </div>
          <AgencyStructureList :active-tab="activeTab" />
        </ModalSection>
      </div>
    </AgencyStructureModalProvider>
  </AgencyStructureProvider>
</template>
